import UploadFileIcon from '@mui/icons-material/UploadFile';
import {
  Alert,
  Button,
  Card,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { useHrRoster, useHrStatus, useHrUpload } from '../../api/queries';
import { shortDate } from '../../lib/format';

// Map flexible xlsx column names → canonical JSON keys the endpoint expects.
// HR sometimes changes headers slightly — normalize once here (same alias
// table as the legacy admin UI).
const HR_COL_ALIASES: Record<string, string[]> = {
  employee_identifier: ['Employee Identifier', 'employee id', 'emp id', 'employee_id'],
  first_name: ['First Name', 'FirstName', 'Given Name'],
  last_name: ['Last Name', 'LastName', 'Surname', 'Family Name'],
  business_email: ['Business email', 'Business Email', 'Email', 'Work Email', 'Email Address'],
  department: ['Department', 'Dept'],
  legal_entity: ['Legal Entity', 'Entity', 'Legal_Entity'],
  seniority_date: ['Seniority Date', 'Seniority'],
  company_classification: ['Company Classification', 'Classification', 'Employee Type'],
  country_code: ['Geo Country Country Code', 'Country Code', 'Geo Country', 'Country'],
  hire_date: ['Hire Date', 'Start Date', 'Hired On'],
  job_title: ['Job Assignment Name', 'Job Title', 'Title', 'Position'],
};

function parseWorkbook(data: ArrayBuffer): Record<string, string | null>[] {
  const wb = XLSX.read(data, { type: 'array', cellDates: false });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json<(string | null)[]>(ws, {
    header: 1,
    raw: false,
    defval: null,
  });
  if (rows.length < 2) throw new Error('Sheet has no data rows');

  const headers = (rows[0] ?? []).map((h) =>
    String(h ?? '')
      .trim()
      .toLowerCase(),
  );
  const colMap: Record<string, number> = {};
  for (const [canon, aliases] of Object.entries(HR_COL_ALIASES)) {
    const idx = headers.findIndex((h) => aliases.some((a) => a.toLowerCase() === h));
    if (idx !== -1) colMap[canon] = idx;
  }
  // Newer People Ops exports drop the email column entirely — the SERVER
  // resolves emails (existing roster → Entra directory → derived), so rows
  // only need either an email or a name pair.
  if (
    colMap.business_email === undefined &&
    (colMap.first_name === undefined || colMap.last_name === undefined)
  ) {
    throw new Error(
      `No email column (and no name columns to resolve one from). Headers seen: ${(rows[0] ?? [])
        .filter(Boolean)
        .join(', ')}`,
    );
  }

  return rows
    .slice(1)
    .map((row) => {
      const out: Record<string, string | null> = {};
      for (const [canon, idx] of Object.entries(colMap)) {
        const v = row[idx];
        out[canon] = v == null || String(v).trim() === '' ? null : String(v).trim();
      }
      return out;
    })
    .filter(
      (r) => (r.business_email && r.business_email.includes('@')) || (r.first_name && r.last_name),
    );
}

export default function AdminHR() {
  const status = useHrStatus();
  const roster = useHrRoster();
  const upload = useHrUpload();
  const { enqueueSnackbar } = useSnackbar();
  const fileInput = useRef<HTMLInputElement>(null);
  const [parseError, setParseError] = useState<string | null>(null);

  const handleFile = async (file: File) => {
    setParseError(null);
    try {
      const rows = parseWorkbook(await file.arrayBuffer());
      if (rows.length === 0) throw new Error('No valid rows (need an email or a name per row)');
      const result = await upload.mutateAsync(rows);
      enqueueSnackbar(`Uploaded ${result.rows_inserted} employees`, { variant: 'success' });
      const src = result.email_sources;
      if (src && (src.roster || src.directory || src.derived)) {
        enqueueSnackbar(
          `Emails resolved: ${src.roster ?? 0} kept from existing roster, ${src.directory ?? 0} from the directory, ${src.derived ?? 0} derived from names — spot-check derived ones`,
          { variant: src.derived ? 'warning' : 'info', autoHideDuration: 12000 },
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setParseError(msg);
      enqueueSnackbar(`Upload failed: ${msg}`, { variant: 'error' });
    } finally {
      if (fileInput.current) fileInput.current.value = '';
    }
  };

  return (
    <Stack spacing={3}>
      <Card variant="outlined" className="p-5 max-w-2xl">
        <Stack spacing={2}>
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              HR headcount upload
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {status.data
                ? `Current roster: ${status.data.count} employees · last upload ${shortDate(status.data.last_upload)}`
                : '…'}
            </Typography>
          </div>
          <Typography variant="caption" color="text.secondary">
            Drop the monthly xlsx export. Expected columns: Employee Identifier, First Name, Last
            Name, Business email, Department, Legal Entity, Seniority Date, Company Classification,
            Country Code, Hire Date, Job Assignment Name. Header match is case-insensitive. This
            REPLACES the whole roster — it&apos;s the spine for &quot;who is an active
            employee&quot;.
          </Typography>
          <div>
            <input
              type="file"
              accept=".xlsx,.xls"
              hidden
              ref={fileInput}
              onChange={(e) => e.target.files?.[0] && handleFile(e.target.files[0])}
            />
            <Button
              variant="contained"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInput.current?.click()}
              disabled={upload.isPending}
            >
              {upload.isPending ? 'Uploading…' : 'Choose xlsx'}
            </Button>
          </div>
          {parseError && <Alert severity="error">{parseError}</Alert>}
        </Stack>
      </Card>

      {roster.isLoading ? (
        <CircularProgress />
      ) : (
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Title</TableCell>
              <TableCell>Entity</TableCell>
              <TableCell>Country</TableCell>
              <TableCell>Hired</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(roster.data ?? []).map((r) => (
              <TableRow key={r.business_email} hover>
                <TableCell>
                  {[r.first_name, r.last_name].filter(Boolean).join(' ') || '—'}
                </TableCell>
                <TableCell>{r.business_email}</TableCell>
                <TableCell>{r.department ?? '—'}</TableCell>
                <TableCell>{r.job_title ?? '—'}</TableCell>
                <TableCell>{r.legal_entity ?? '—'}</TableCell>
                <TableCell>{r.country_code ?? '—'}</TableCell>
                <TableCell>{shortDate(r.hire_date)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Stack>
  );
}
